import { useMemo, useState } from 'react';
import RewardConfigurator from './RewardConfigurator';
import { useApi } from '../lib/api';
import { projectDraftCampaign } from '../lib/costProjection';
import { clampMilestoneRates } from '../lib/milestoneRates';
import { t, tp } from '../lib/i18n';
import type { Campaign, OnHardCap, RewardInput } from '../lib/types';

interface Props {
  onClose: () => void;
  onCreated: (campaign: Campaign) => void;
}

const TRIGGER_EVENTS: Array<{ value: string; meta: string[] }> = [
  { value: 'platform_registration', meta: [] },
  { value: 'app_installation', meta: ['appId'] },
  { value: 'first_payment', meta: ['appId', 'minAmountEur'] },
  { value: 'login_milestone', meta: ['appId', 'loginCount'] },
  { value: 'plan_upgrade', meta: ['appId', 'planId'] },
  { value: 'review_submitted', meta: ['appId'] },
  { value: 'custom', meta: [] },
];

const HARD_CAP_VALUES: OnHardCap[] = [
  'stop_lucky_draw_only',
  'pause_campaign',
  'notify_only',
];

const TOTAL_STEPS = 5;

export default function CampaignWizard({ onClose, onCreated }: Props) {
  const api = useApi();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('https://dotbots.boutique/register');
  const [triggerEvent, setTriggerEvent] = useState('platform_registration');
  const [triggerEventMeta, setTriggerEventMeta] = useState<Record<string, string>>({});

  const [startsAt, setStartsAt] = useState(new Date().toISOString().slice(0, 16));
  const [durationDays, setDurationDays] = useState(14);
  const [luckyDrawRatio, setLuckyDrawRatio] = useState(20);

  const [rewards, setRewards] = useState<RewardInput[]>([]);

  // Budget — every layer optional. Defaults match backend defaults.
  const [enableTotalBudget, setEnableTotalBudget] = useState(false);
  const [budgetTotalEur, setBudgetTotalEur] = useState<number>(2000);
  const [budgetSoftCapPercent, setBudgetSoftCapPercent] = useState<number>(80);
  const [budgetOnHardCap, setBudgetOnHardCap] = useState<OnHardCap>('stop_lucky_draw_only');

  const [enableDailyCap, setEnableDailyCap] = useState(false);
  const [budgetDailyCapEur, setBudgetDailyCapEur] = useState<number>(100);

  const endsAt = useMemo(() => {
    const d = new Date(startsAt);
    d.setUTCDate(d.getUTCDate() + durationDays);
    return d.toISOString();
  }, [startsAt, durationDays]);

  const triggerSpec = TRIGGER_EVENTS.find((te) => te.value === triggerEvent)!;

  // Step 5 simulator state — live cost projection before launching.
  const [simTarget, setSimTarget] = useState(1000);
  const [simRates, setSimRates] = useState<Record<string, number>>({
    '3': 0.15,
    '5': 0.06,
  });

  const milestoneRewardValues = useMemo(
    () =>
      Array.from(
        new Set(
          rewards
            .filter((r) => r.triggerType === 'referral_milestone' && r.triggerValue != null)
            .map((r) => String(r.triggerValue)),
        ),
      ).sort((a, b) => Number(a) - Number(b)),
    [rewards],
  );

  // Make sure every milestone has a default rate when the user reaches step 5.
  const effectiveRates = useMemo(() => {
    const merged = { ...simRates };
    for (const k of milestoneRewardValues) {
      if (merged[k] === undefined) merged[k] = 0.1;
    }
    return merged;
  }, [simRates, milestoneRewardValues]);

  const projection = useMemo(() => {
    if (rewards.length === 0 || simTarget <= 0) return null;
    return projectDraftCampaign(
      { luckyDrawRatio, startsAt: new Date(startsAt).toISOString(), endsAt },
      rewards,
      { targetParticipants: simTarget, milestoneRates: effectiveRates },
    );
  }, [rewards, simTarget, luckyDrawRatio, startsAt, endsAt, effectiveRates]);

  const stepValid = (s: number): boolean => {
    if (s === 1) {
      return Boolean(
        name && productName && productDescription && targetAudience && registrationUrl,
      );
    }
    if (s === 2) return durationDays > 0;
    if (s === 3) return rewards.length > 0;
    if (s === 4) return true;
    return true;
  };

  async function submit(launch: boolean) {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name,
        productName,
        productDescription,
        targetAudience,
        registrationUrl,
        triggerEvent,
        triggerEventMeta: triggerSpec.meta.length > 0 ? triggerEventMeta : null,
        luckyDrawRatio,
        startsAt: new Date(startsAt).toISOString(),
        endsAt,
        rewards,
        budgetTotalEur: enableTotalBudget ? budgetTotalEur : null,
        budgetSoftCapPercent: enableTotalBudget ? budgetSoftCapPercent : null,
        budgetOnHardCap: enableTotalBudget ? budgetOnHardCap : null,
        budgetDailyCapEur: enableDailyCap ? budgetDailyCapEur : null,
      };
      const res = await api<{ campaign: Campaign }>('/api/campaigns', {
        method: 'POST',
        body,
      });
      if (launch) {
        await api(`/api/campaigns/${res.campaign.id}/launch`, { method: 'POST' });
      }
      onCreated(res.campaign);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const softCapAmount = enableTotalBudget
    ? Math.round(budgetTotalEur * (budgetSoftCapPercent / 100))
    : 0;

  const rewardsWithMaxCount = rewards.filter((r) => r.maxWinners != null).length;

  const luckyDrawLabel =
    luckyDrawRatio === 0
      ? t('step2.luckyDrawOff')
      : tp('step2.luckyDrawOn', { n: luckyDrawRatio });

  const scenarioLabel: Record<'conservative' | 'expected' | 'optimistic', string> = {
    conservative: t('scenario.conservative'),
    expected: t('scenario.expected'),
    optimistic: t('scenario.optimistic'),
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex-between" style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>
            {tp('wizard.titleStep', { step, total: TOTAL_STEPS })}
          </h2>
          <button className="secondary" onClick={onClose}>{t('common.close')}</button>
        </div>

        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {step === 1 && (
          <div className="grid-2">
            <div>
              <label>{t('step1.campaignName')}</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('step1.campaignNamePlaceholder')}
              />
            </div>
            <div>
              <label>{t('step1.productName')}</label>
              <input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder={t('step1.productNamePlaceholder')}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>{t('step1.productDescription')}</label>
              <textarea
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                rows={4}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>{t('step1.targetAudience')}</label>
              <textarea
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                rows={2}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>{t('step1.registrationUrl')}</label>
              <input value={registrationUrl} onChange={(e) => setRegistrationUrl(e.target.value)} />
              <div className="small muted" style={{ marginTop: '0.25rem' }}>
                {t('step1.urlHintPrefix')} <span className="kbd">?ref=</span> &amp;{' '}
                <span className="kbd">?campaign=</span> {t('step1.urlHintSuffix')}
              </div>
            </div>
            <div>
              <label>{t('step1.triggerEvent')}</label>
              <select
                value={triggerEvent}
                onChange={(e) => {
                  setTriggerEvent(e.target.value);
                  setTriggerEventMeta({});
                }}
              >
                {TRIGGER_EVENTS.map((te) => (
                  <option key={te.value} value={te.value}>{t(`triggerEvent.${te.value}`)}</option>
                ))}
              </select>
            </div>
            {triggerSpec.meta.map((field) => (
              <div key={field}>
                <label>{field}</label>
                <input
                  value={triggerEventMeta[field] ?? ''}
                  onChange={(e) =>
                    setTriggerEventMeta((m) => ({ ...m, [field]: e.target.value }))
                  }
                  placeholder={field === 'minAmountEur' || field === 'loginCount' ? '0' : ''}
                />
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid-2">
            <div>
              <label>{t('step2.startsAt')}</label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
            </div>
            <div>
              <label>{t('step2.duration')} {durationDays}</label>
              <input
                type="range"
                min={1}
                max={90}
                value={durationDays}
                onChange={(e) => setDurationDays(Number(e.target.value))}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>{t('step2.luckyDrawRatio')} {luckyDrawLabel}</label>
              <input
                type="range"
                min={0}
                max={50}
                value={luckyDrawRatio}
                onChange={(e) => setLuckyDrawRatio(Number(e.target.value))}
              />
              <div className="small muted">{t('step2.luckyDrawHint')}</div>
            </div>
            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <div className="muted small">{t('step2.endsOn')}</div>
              <div style={{ fontWeight: 600 }}>{new Date(endsAt).toLocaleString()}</div>
            </div>
          </div>
        )}

        {step === 3 && (
          <RewardConfigurator
            rewards={rewards}
            onChange={setRewards}
            campaignLuckyDrawRatio={luckyDrawRatio}
          />
        )}

        {step === 4 && (
          <div>
            <h3 style={{ marginTop: 0 }}>{t('budget.title')}</h3>
            <p className="muted small">{t('budget.subtitle')}</p>

            <section className="card" style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ marginTop: 0 }}>{t('budget.layer1Title')}</h4>
              <p className="small muted">{t('budget.layer1Description')}</p>
              <p className="small">
                {rewardsWithMaxCount > 0
                  ? tp('budget.layer1Configured', {
                      set: rewardsWithMaxCount,
                      total: rewards.length,
                    })
                  : t('budget.layer1NotConfigured')}
              </p>
            </section>

            <section className="card" style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ marginTop: 0 }}>{t('budget.layer2Title')}</h4>
              <label className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enableDailyCap}
                  onChange={(e) => setEnableDailyCap(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                {t('budget.dailyToggle')}
              </label>
              {enableDailyCap && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label>{t('budget.dailyAmount')}</label>
                  <input
                    type="number"
                    min={0}
                    step={10}
                    value={budgetDailyCapEur}
                    onChange={(e) => setBudgetDailyCapEur(Number(e.target.value))}
                  />
                  <div className="small muted" style={{ marginTop: '0.25rem' }}>
                    {t('budget.dailyHint')}
                  </div>
                </div>
              )}
            </section>

            <section className="card" style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ marginTop: 0 }}>{t('budget.layer3Title')}</h4>
              <label className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={enableTotalBudget}
                  onChange={(e) => setEnableTotalBudget(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                {t('budget.totalToggle')}
              </label>
              {enableTotalBudget && (
                <div className="grid-2" style={{ marginTop: '0.75rem' }}>
                  <div>
                    <label>{t('budget.totalAmount')}</label>
                    <input
                      type="number"
                      min={0}
                      step={50}
                      value={budgetTotalEur}
                      onChange={(e) => setBudgetTotalEur(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label>
                      {tp('budget.softCap', {
                        pct: budgetSoftCapPercent,
                        amount: softCapAmount,
                      })}
                    </label>
                    <input
                      type="range"
                      min={50}
                      max={95}
                      step={5}
                      value={budgetSoftCapPercent}
                      onChange={(e) => setBudgetSoftCapPercent(Number(e.target.value))}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label>{t('budget.hardCapAction')}</label>
                    <select
                      value={budgetOnHardCap}
                      onChange={(e) => setBudgetOnHardCap(e.target.value as OnHardCap)}
                    >
                      {HARD_CAP_VALUES.map((v) => (
                        <option key={v} value={v}>{t(`budget.hardCap.${v}`)}</option>
                      ))}
                    </select>
                    <div className="small muted" style={{ marginTop: '0.5rem' }}>
                      {t(`budget.hint.${budgetOnHardCap}`)}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {step === 5 && (
          <div>
            <h3 style={{ marginTop: 0 }}>{t('summary.title')}</h3>
            <table>
              <tbody>
                <tr><th>{t('col.name')}</th><td>{name}</td></tr>
                <tr><th>{t('summary.product')}</th><td>{productName}</td></tr>
                <tr>
                  <th>{t('col.trigger')}</th>
                  <td>{t(`triggerEvent.${triggerEvent}`)}</td>
                </tr>
                <tr>
                  <th>{t('summary.start')}</th>
                  <td>{new Date(startsAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <th>{t('summary.ends')}</th>
                  <td>{new Date(endsAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <th>{t('summary.luckyDraw')}</th>
                  <td>{luckyDrawLabel}</td>
                </tr>
                <tr>
                  <th>{t('summary.rewardCount')}</th>
                  <td>{rewards.length}</td>
                </tr>
                <tr>
                  <th>{t('summary.perRewardMax')}</th>
                  <td>
                    {rewardsWithMaxCount > 0
                      ? tp('summary.perRewardMaxValue', { n: rewardsWithMaxCount })
                      : t('summary.none')}
                  </td>
                </tr>
                <tr>
                  <th>{t('summary.dailyCap')}</th>
                  <td>
                    {enableDailyCap
                      ? tp('summary.dailyCapValue', { amount: budgetDailyCapEur })
                      : t('summary.none')}
                  </td>
                </tr>
                <tr>
                  <th>{t('summary.totalBudget')}</th>
                  <td>
                    {enableTotalBudget
                      ? tp('summary.totalBudgetValue', {
                          total: budgetTotalEur,
                          soft: softCapAmount,
                          action: t(`budget.hardCap.${budgetOnHardCap}`),
                        })
                      : t('summary.none')}
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 style={{ marginTop: '1.5rem' }}>{t('sim.title')}</h3>
            <p className="muted small">{t('sim.description')}</p>

            <div className="card" style={{ marginBottom: '0.75rem' }}>
              <div className="grid-2">
                <div>
                  <label>{t('sim.targetSignups')}</label>
                  <input
                    type="number"
                    min={1}
                    value={simTarget}
                    onChange={(e) => setSimTarget(Number(e.target.value))}
                  />
                </div>
              </div>

              {milestoneRewardValues.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <label>{t('sim.milestoneRates')}</label>
                  <div className="grid-3">
                    {milestoneRewardValues.map((v) => {
                      const rate = effectiveRates[v] ?? 0;
                      return (
                        <div key={v} className="card" style={{ padding: '0.6rem' }}>
                          <div className="small muted">
                            {tp('sim.milestoneSliderLabel', {
                              n: v,
                              pct: (rate * 100).toFixed(1),
                            })}
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={50}
                            step={0.5}
                            value={rate * 100}
                            onChange={(e) =>
                              setSimRates((prev) =>
                                clampMilestoneRates(
                                  prev,
                                  milestoneRewardValues,
                                  v,
                                  Number(e.target.value) / 100,
                                ),
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {projection ? (
              <>
                <div className="card" style={{ marginBottom: '0.75rem' }}>
                  <table className="responsive-table">
                    <thead>
                      <tr>
                        <th>{t('sim.col.reward')}</th>
                        <th style={{ textAlign: 'right' }}>{t('sim.col.winners')}</th>
                        <th style={{ textAlign: 'right' }}>{t('sim.col.costPerWinner')}</th>
                        <th style={{ textAlign: 'right' }}>{t('sim.col.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projection.rewardProjections.map((p, i) => (
                        <tr key={i}>
                          <td data-label={t('sim.col.reward')}>
                            <span>
                              {p.rewardLabel || (
                                <span className="muted">{t('rewards.noLabel')}</span>
                              )}
                              <div className="small muted">
                                {p.triggerType}
                                {p.triggerValue ? ` (${p.triggerValue})` : ''}
                              </div>
                            </span>
                          </td>
                          <td
                            data-label={t('sim.col.winners')}
                            style={{ textAlign: 'right' }}
                          >
                            <span>
                              {p.projectedWinners.toFixed(1)}
                              {p.cappedByMax && (
                                <span
                                  className="badge"
                                  title={t('sim.capTitle')}
                                  style={{ marginLeft: '0.5rem' }}
                                >
                                  {t('sim.cap')}
                                </span>
                              )}
                            </span>
                          </td>
                          <td
                            data-label={t('sim.col.costPerWinner')}
                            style={{ textAlign: 'right' }}
                          >
                            €{p.realCostEur.toFixed(2)}
                          </td>
                          <td
                            data-label={t('sim.col.total')}
                            style={{ textAlign: 'right' }}
                          >
                            €{p.projectedCost.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={3} style={{ textAlign: 'right' }}>
                          {t('sim.totalRow')}
                        </th>
                        <th style={{ textAlign: 'right' }}>
                          €{projection.summary.totalProjectedCost.toFixed(2)}
                        </th>
                      </tr>
                      <tr>
                        <th colSpan={3} style={{ textAlign: 'right' }}>
                          {t('sim.perSignupRow')}
                        </th>
                        <th style={{ textAlign: 'right' }}>
                          €{projection.summary.costPerParticipant.toFixed(2)}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {projection.rewardProjections.some((p) => p.cappedByMax) && (
                  <div className="notice small" style={{ marginBottom: '0.75rem' }}>
                    {t('sim.capNotice')}
                  </div>
                )}

                {enableTotalBudget &&
                  projection.summary.totalProjectedCost > budgetTotalEur && (
                    <div className="error small" style={{ marginBottom: '0.75rem' }}>
                      {tp('sim.budgetExceeded', {
                        cost: projection.summary.totalProjectedCost.toFixed(2),
                        budget: budgetTotalEur,
                        action: t(`budget.hardCap.${budgetOnHardCap}`),
                      })}
                    </div>
                  )}

                <div className="grid-3">
                  {(['conservative', 'expected', 'optimistic'] as const).map((scenario) => {
                    const s = projection.summary.scenarios[scenario];
                    return (
                      <div className="card" key={scenario}>
                        <div className="stat-label">{scenarioLabel[scenario]}</div>
                        <div className="stat-value">€{s.totalCost.toFixed(2)}</div>
                        <div className="muted small">
                          {tp('scenario.signupsAndPer', {
                            n: s.participants,
                            cost: s.costPerParticipant.toFixed(2),
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="muted small">
                {rewards.length === 0
                  ? t('sim.empty.noRewards')
                  : t('sim.empty.noTarget')}
              </div>
            )}
          </div>
        )}

        <div className="flex-between" style={{ marginTop: '1.25rem' }}>
          <button
            className="secondary"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
          >
            {t('common.previous')}
          </button>
          <div className="row">
            {step < TOTAL_STEPS && (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!stepValid(step)}
              >
                {t('common.next')}
              </button>
            )}
            {step === TOTAL_STEPS && (
              <>
                <button className="secondary" disabled={saving} onClick={() => submit(false)}>
                  {t('wizard.saveDraft')}
                </button>
                <button disabled={saving} onClick={() => submit(true)}>
                  {t('wizard.launch')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
